// Sentence-by-sentence TTS with a pipeline: as soon as a sentence is emitted
// by the streaming chat, we IMMEDIATELY kick off its /api/tts fetch and
// enqueue the promise. Playback then consumes the queue in order — so
// sentence N+1's audio is already downloading (or ready) while sentence N
// is playing. This removes the audible gaps the user reported.
//
// iOS Safari standalone gotchas:
//   1. Audio.play() must first be triggered inside a real user gesture,
//      otherwise subsequent async plays are silently blocked. unlock() plays
//      a tiny silent WAV on a tap so future src swaps play without a fresh
//      gesture.
//   2. We reuse ONE <audio> element across the whole session; iOS unlocks
//      per-element.
//   3. Blob URLs are revoked after each sentence.
import { ttsBlob } from "./api";

const SILENT_WAV =
  "data:audio/wav;base64,UklGRlwAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YTgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==";

export function createSpeaker({ voice = "shimmer" } = {}) {
  let muted = false;
  let unlocked = false;
  let cancelled = false;
  let playing = false;
  /** Queue of in-flight Blob promises for each sentence, in order. */
  const queue = [];
  const listeners = new Set();
  const emit = (s) => listeners.forEach((l) => l(s));

  const audio = typeof Audio !== "undefined" ? new Audio() : null;
  if (audio) {
    audio.preload = "auto";
    audio.setAttribute("playsinline", "");
    audio.setAttribute("webkit-playsinline", "");
  }
  let currentUrl = null;

  async function drain() {
    if (!audio || playing || cancelled) return;
    const promise = queue.shift();
    if (!promise) {
      emit({ speaking: false });
      return;
    }
    playing = true;
    emit({ speaking: true });
    let blob = null;
    try {
      blob = await promise;
    } catch {
      /* fetch failed — skip this sentence, keep the flow going */
    }
    if (blob && !cancelled && !muted) {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
        currentUrl = null;
      }
      currentUrl = URL.createObjectURL(blob);
      audio.src = currentUrl;
      await new Promise((resolve) => {
        const done = () => {
          audio.onended = null;
          audio.onerror = null;
          resolve();
        };
        audio.onended = done;
        audio.onerror = done;
        // If iOS didn't grant playback (no gesture chain), .play() rejects.
        // Swallow so chat stays usable text-only.
        const p = audio.play();
        if (p && typeof p.catch === "function") p.catch(done);
      });
    }
    playing = false;
    if (!cancelled) drain();
  }

  return {
    /** Call from a real user gesture (tap). Idempotent. */
    unlock() {
      if (unlocked || !audio) return;
      audio.src = SILENT_WAV;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch {
            /* ignore */
          }
          unlocked = true;
        }).catch(() => {
          /* Not unlocked yet — will retry on next gesture. */
        });
      } else {
        unlocked = true;
      }
    },
    /** Enqueue a sentence. Fetches audio immediately (pipelined). */
    speak(sentence) {
      if (!sentence || muted) return;
      // Kick off the fetch RIGHT NOW so it overlaps with current playback.
      const p = ttsBlob(sentence, voice).catch(() => null);
      queue.push(p);
      drain();
    },
    stop() {
      cancelled = true;
      queue.length = 0;
      if (audio) {
        try {
          audio.pause();
        } catch {
          /* ignore */
        }
      }
      playing = false;
      emit({ speaking: false });
    },
    setMuted(v) {
      muted = v;
      if (v) this.stop();
      // Allow future speak() after unmute.
      cancelled = false;
    },
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
